import {
  ref,
  defineComponent,
  onMounted,
  reactive,
  PropType,
  watch,
  onBeforeUnmount
} from 'vue';
import {
  ElForm,
  ElFormItem,
  ElInput,
  ElSelect,
  ElCol,
  ElRow,
  ElRadioGroup,
  ElRadio,
  dayjs,
  ElDatePicker,
  ElOption,
  ElTreeSelect,
  ElUpload,
  ElIcon,
  ElDialog,
  UploadProps,
  ElButton,
  ElMessage,
  ElInputNumber,
} from 'element-plus';
import type { FormInstance } from 'element-plus';
import {
  checkToken,
  getTakeoverReasonList,
  getVehicleStatus,
  uploadImage,
  reportProblem,
} from '@/service';
import { getFeishuMembers, getField } from '@/service/feishu';
// import { useStore } from '@/store';
import { getQueryVariable, debounce } from '@/utils/utils';
import { Opportunity, Plus } from '@element-plus/icons-vue';
import { useFrameDataStore } from "@/store/frameDataStore";

// 负责组飞书字段 field_key
const GROUP_FIELD_KEY = 'field_2cfcf7';

const customUsecaseList = [
  'QA测试',
  '运营',
  '运营测试',
  '耐久测试',
  '未知状态',
];

const priorityLevelList = [
  {
    key: 'option_1',
    name: 'P0：严重事故，一日内解决',
  },
  {
    key: 'option_2',
    name: 'P1：快速需求/风险，5日内解决',
  },
  {
    key: 'option_3',
    name: 'P2：正常迭代',
  },
  {
    key: 'option_895170126709555200',
    name: 'P3：待定',
  },
  {
    key: 'option_895170126709555201',
    name: 'P4：待定',
  },
  {
    key: 'option_894865831581745152',
    name: 'P5：数据采集',
  },
];

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const props = {
  onSubmitSuccess: {
    type: Function as PropType<() => void>,
    default: () => { },
  },
};

const SubmitProblem = defineComponent({
  name: 'SubmitProblem',
  props,
  setup(props) {
    let frameDataStore = useFrameDataStore();
    const { onSubmitSuccess } = props;
    // const store = useStore();

    const takeoverReasons = ref<
      {
        label: string;
        value: string;
      }[]
    >([]);

    const formData = reactive({
      form: {
        role: customUsecaseList[0],
        road: '社会',
        postbackData: 'option_1',
        priority: 'option_2',
        occurDateTime: dayjs(frameDataStore.currentTime).valueOf(),
        duration: 0,
        vehicleName: getQueryVariable('vehicleId'),
        version: '',
        bugMaker: '',
        content: '',
        // 研发人员（飞书 user_key，单选）/ 负责组（飞书选项 value，单选）
        ownerUser: '',
        ownerGroup: '',
        phenomenonClassification: void 0,
        title: '',
        dnnVersion: void 0,
        mapVersion: void 0,
      },
    });

    const formRef = ref();

    const fileList = reactive<any>({ value: [] });

    const dialogImageUrl = ref('');
    const dialogVisible = ref(false);

    // 研发人员搜索结果（飞书成员）
    const ownerUserOptions = ref<{ label: string; value: string }[]>([]);
    const ownerUserLoading = ref(false);
    // 负责组选项（飞书字段透传，保留父/子树结构）
    const ownerGroupOptions = ref<any[]>([]);

    // 研发人员：输入后远程搜索飞书成员（防抖）
    const searchOwnerUser = debounce((query: string) => {
      if (!query) {
        ownerUserOptions.value = [];
        return;
      }
      ownerUserLoading.value = true;
      getFeishuMembers({ query })
        .then((res: any) => {
          ownerUserOptions.value = (res?.data || []).map((item: any) => ({
            label: item.name_cn,
            value: item.user_key,
          }));
        })
        .finally(() => {
          ownerUserLoading.value = false;
        });
    }, 800);

    const rules = reactive({
      occurDateTime: [
        { required: true, trigger: 'change', message: '请选择发生时间' },
      ],
      ownerUser: [
        { required: true, trigger: 'change', message: '请选择研发人员' },
      ],
      ownerGroup: [
        { required: true, trigger: 'change', message: '请选择负责组' },
      ],
      phenomenonClassification: [
        { required: true, trigger: 'change', message: '请选择现象分类' },
      ],
      title: [{ required: true, trigger: 'change', message: '请输入标题' }],
      role: [{ required: true, trigger: 'change', message: '请选择车辆状态' }],
      priority: [
        { required: true, trigger: 'change', message: '请选择优先级' },
      ],
    });

    onMounted(() => {
      // 因为有输入， 先把对n的监听停掉
      // store.commit('wsModule/setNextFrameMode', false);

      checkToken().then((res) => (formData.form.bugMaker = res.data.user_name));

      getTakeoverReasonList().then((res) => {
        takeoverReasons.value = res.data.list.map((item: any) => ({
          ...item,
          label: item.dictName,
          value: item.dictName,
        }));
      });
      getVehicleInfo(Math.floor(frameDataStore.currentTime));

      // 负责组下拉：由飞书字段接口透传
      getField()
        .then((res: any) => {
          const field = (res?.data || []).find(
            (f: any) => f.field_key === GROUP_FIELD_KEY,
          );
          // 保留父/子树结构，供 ElTreeSelect 单选使用
          ownerGroupOptions.value = field?.options || [];
        })
        .catch(() => {
          ElMessage.error('获取负责组选项失败');
        });

      // 监听按键
      window.addEventListener('keydown', handleKeydown);
    });

    onBeforeUnmount(()=>{
      window.removeEventListener('keydown', handleKeydown); 
    })

    watch(
      () => formData.form.occurDateTime,
      (time) => {
        getVehicleInfo(dayjs(time).valueOf());
      },
    );

    const handleKeydown = async (event) => {  
      if (event.ctrlKey && event.key === 'v') {  
        event.preventDefault();  
        const blob = await getClipboard();  
        if (blob) {  
          const file = new File([blob], `下载.png`, { type: 'image/png' }); 
          onUpload({ file }); 
        } else {  
          console.error("剪贴板没有可用的图像数据，请检查并重试。");  
        }  
      }  
    }

    const getClipboard  = async()=>{
      let blob
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (type.startsWith('image/')) { 
              blob = await clipboardItem.getType(type);  
              return blob; 
            }
          }
        }
      } catch (err) {
        console.error(err.name, err.message);
      }
      return blob;
    }

    const getVehicleInfo = (time: number) =>
      getVehicleStatus(getQueryVariable('vehicleId'), time).then((res) => {
        const { zoeVersion, zoeMapVersion, zoeDnnModelVersion } = res.data;
        formData.form.version = zoeVersion;
        formData.form.dnnVersion = zoeDnnModelVersion;
        formData.form.mapVersion = zoeMapVersion;
      });

    const onUpload = ({ file }: { file: File }) => {
      const formData = new FormData();

      formData.append('file', file);

      return uploadImage(formData).then((resp: any) => {
        if (resp.success) {
          getBase64(file).then((url) => {
            fileList.value.push({
              url,
              ...file,
              ...resp.data,
              originalFilename: resp.data.key,
            });
          });
          return true;
        }
      });
    };

    const handleRemove: UploadProps['beforeRemove'] = (uploadFile: any) => {
      const index = fileList.value.findIndex(
        (item: any) => item.uid === uploadFile.uid,
      );
      if(index!==-1){
        fileList.value.splice(index, 1);
      }
      return false;
    };

    const handlePictureCardPreview: UploadProps['onPreview'] = (uploadFile) => {
      dialogImageUrl.value = uploadFile.url!;
      dialogVisible.value = true;
    };

    const onSubmitClick = (formEl: FormInstance) => {
      formEl?.validate((valid) => {
        if (!valid) {
          return false;
        }

        const {
          version,
          occurDateTime,
          road,
          title,
          bugMaker,
          ownerUser,
          ownerGroup,
          postbackData,
          dnnVersion,
          mapVersion,
          duration,
          phenomenonClassification,
          priority,
          content,
          role,
        } = formData.form;

        // 保持原 payload 格式：owner 仍为「组-人」组合值
        const owner = `${ownerGroup}-${ownerUser}`;

        const params = {
          zoeVersion: version,
          useStuckTime:
            occurDateTime?.valueOf() ===
            dayjs(frameDataStore.currentTime).valueOf(),
          title: `${dayjs(occurDateTime).format(
            'YYYY-MM-DD HH:mm:ss',
          )}|${getQueryVariable(
            'vehicleId',
          )}|3D回放提报|${title.trim()}`,
          occurDateTime: dayjs(occurDateTime).valueOf(),
          occurEndTime: dayjs(occurDateTime).valueOf() + 20 * 1000,
          bugMaker,
          vehicleName: getQueryVariable('vehicleId'),
          postbackData,
          version,
          duration,
          phenomenonClassification,
          priority,
          content,
          problemImages: fileList.value.map((file: any) => ({
            contentType: file.contentType,
            key: file.key,
            originalFilename: file.originalFilename, 
          })),
          role,
          ownerUser,
          ownerGroup,
          dnnVersion,
          mapVersion,
          owner,
          playback: true,
        };
        
        reportProblem(params).then(() => {
          ElMessage.success({
            dangerouslyUseHTMLString: true,
            message: `提交成功`,
          });

          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        });
      });
    };

    const onPaste = (e: ClipboardEvent) => {
      const type = e?.clipboardData?.items?.[0]?.type;
      const isJpgOrPng = type === 'image/jpeg' || type === 'image/png';

      if (!!type && isJpgOrPng) {
        const file = e?.clipboardData?.items?.[0]?.getAsFile()!;

        if (!file) {
          console.warn('未检测到粘贴文件');
          return;
        }

        const formData = new FormData();

        formData.append('file', file);

        return uploadImage(formData).then((resp: any) => {
          if (resp.success) {
            getBase64(file).then((url) => {
              fileList.value.push({
                url,
                ...file,
                ...resp.data,
                originalFilename: resp.data.key,
              });
            });

            return true;
          }
        });
      }
    };

    const stopPropagation = (event: KeyboardEvent) => {
      event.stopPropagation();
    };

    return {
      formData,
      rules,
      takeoverReasons,
      fileList,
      dialogImageUrl,
      dialogVisible,
      handleRemove,
      handlePictureCardPreview,
      formRef,
      onSubmitClick,
      onUpload,
      onPaste,
      stopPropagation,
      handleKeydown,
      ownerUserOptions,
      ownerUserLoading,
      ownerGroupOptions,
      searchOwnerUser,
    };
  },
  render() {
    return (
      <>
        <ElForm
          inline
          labelPosition="right"
          model={this.formData.form}
          rules={this.rules}
          ref="formRef"
          statusIcon
          onkeydown={this.stopPropagation}
        >
          {/* <ElRow>
            <ElCol span={12}>
              <ElFormItem label="提测人" labelWidth={110} prop="bugMaker">
                <ElInput
                  v-model={this.formData.form.bugMaker}
                  disabled
                  style={{
                    width: '280px',
                  }}
                />
              </ElFormItem>
            </ElCol>
            <ElCol span={12}>
              <ElFormItem label="车辆号" labelWidth={110} prop="vehicleName">
                <ElInput
                  v-model={this.formData.form.vehicleName}
                  disabled
                  style={{
                    width: '280px',
                  }}
                />
              </ElFormItem>
            </ElCol>
          </ElRow> */}
          {/* <ElRow>
            <ElCol span={12}>
              <ElFormItem label="版本信息" labelWidth={110} prop="version">
                <ElInput
                  style={{
                    width: '280px',
                  }}
                  disabled
                  v-model={this.formData.form.version}
                />
              </ElFormItem>
            </ElCol>
            <ElCol span={12}>
              <ElFormItem label="车辆状态" labelWidth={110} prop="role">
                <ElSelect
                  v-model={this.formData.form.role}
                  style={{
                    width: '280px',
                  }}
                >
                  {customUsecaseList.map((item) => (
                    <ElOption key={item} value={item}>
                      {item}
                    </ElOption>
                  ))}
                </ElSelect>
              </ElFormItem>
            </ElCol>
          </ElRow> */}
          <ElRow>
            <ElCol span={12}>
              <ElFormItem
                label="发生时间"
                labelWidth={110}
                prop="occurDateTime"
              >
                <ElDatePicker
                  style={{
                    width: '280px',
                  }}
                  type="datetime"
                  v-model={this.formData.form.occurDateTime}
                />
              </ElFormItem>
            </ElCol>
            {/* <ElCol span={12}>
              <ElFormItem
                label="持续时长（分）"
                labelWidth={110}
                prop="duration"
              >
                <ElInputNumber
                  min={0}
                  v-model={this.formData.form.duration}
                  style={{
                    width: '280px',
                  }}
                  controls-position="right"
                />
              </ElFormItem>
            </ElCol> */}
            <ElCol span={12}>
              <ElFormItem label="研发人员" labelWidth={110} prop="ownerUser">
                <ElSelect
                  v-model={this.formData.form.ownerUser}
                  style={{
                    width: '280px',
                  }}
                  placeholder="输入姓名搜索"
                  filterable
                  remote
                  reserveKeyword
                  loading={this.ownerUserLoading}
                  remoteMethod={this.searchOwnerUser}
                >
                  {this.ownerUserOptions.map(({ value, label }) => (
                    <ElOption key={value} label={label} value={value} />
                  ))}
                </ElSelect>
              </ElFormItem>
            </ElCol>
          </ElRow>
          <ElRow>
            <ElCol span={12}>
              <ElFormItem label="负责组" labelWidth={110} prop="ownerGroup">
                <ElTreeSelect
                  v-model={this.formData.form.ownerGroup}
                  style={{ width: '280px' }}
                  placeholder="请选择"
                  data={this.ownerGroupOptions}
                  props={{
                    label: 'label',
                    children: 'children',
                    // 有子节点的父级禁止选中，只能选叶子节点
                    disabled: (data: any) => !!(data.children?.length),
                  }}
                  nodeKey="value"
                  checkStrictly
                  filterable
                  defaultExpandAll
                  renderAfterExpand={false}
                />
              </ElFormItem>
            </ElCol>
          </ElRow>
          {/* <ElRow> */}
            {/* <ElCol span={12}>
              <ElFormItem
                label="现象分类"
                labelWidth={110}
                prop="phenomenonClassification"
              >
                <ElSelect
                  v-model={this.formData.form.phenomenonClassification}
                  style={{
                    width: '280px',
                  }}
                  placeholder="请选择"
                  filterable
                >
                  {this.takeoverReasons.map(({ value, label }) => (
                    <ElOption key={value} label={label} value={value} />
                  ))}
                </ElSelect>
              </ElFormItem>
            </ElCol> */}
          {/* </ElRow> */}
          <ElRow>
            <ElCol span={12}>
              <ElFormItem label="优先等级" labelWidth={110} prop="priority">
                <ElRadioGroup v-model={this.formData.form.priority}>
                  <ElRadio value="option_1">P0</ElRadio>
                  <ElRadio value="option_2">P1</ElRadio>
                  <ElRadio value="option_3">P2</ElRadio>
                </ElRadioGroup>
              </ElFormItem>
            </ElCol>
            <ElCol span={12}>
              <ElFormItem label="上传数据" labelWidth={110} prop="postbackData">
                <ElRadioGroup v-model={this.formData.form.postbackData}>
                  <ElRadio value="option_1">Yes</ElRadio>
                  <ElRadio value="option_2">No</ElRadio>
                </ElRadioGroup>
              </ElFormItem>
            </ElCol>
          </ElRow>
          <ElRow>
            {/* <ElCol span={12}>
              <ElFormItem label="道路信息" labelWidth={110} prop="road">
                <ElRadioGroup v-model={this.formData.form.road}>
                  <ElRadio value="社会">社会道路</ElRadio>
                  <ElRadio value="园区">园区道路</ElRadio>
                </ElRadioGroup>
              </ElFormItem>
            </ElCol> */}
            {/* <ElCol span={12}>
              <ElFormItem label="手机号" labelWidth={110} prop="phone">
                <ElInput
                  style={{
                    width: '280px',
                  }}
                >
                  <template>
                    <ElSelect></ElSelect>
                  </template>
                </ElInput>
              </ElFormItem>
            </ElCol> */}
          </ElRow>
          <ElRow>
            <ElCol span={24}>
            <ElFormItem label="问题标题" labelWidth={110} prop="title">
              <ElInput
                v-model={this.formData.form.title}
                style={{
                  width: '720px',
                }}
                onPaste={this.onPaste}
              />
            </ElFormItem>
            </ElCol>
          </ElRow>
          {/* <ElRow>
            <ElFormItem label="问题描述" labelWidth={110} prop="content">
              <ElInput
                v-model={this.formData.form.content}
                autosize={{ minRows: 2 }}
                type="textarea"
                style={{
                  width: '720px',
                }}
                // @ts-ignore
                onPaste={this.onPaste}
              />
            </ElFormItem>
          </ElRow> */}
          <ElFormItem label="附件" labelWidth={110} prop="attachment">
            <ElUpload
              onPreview={this.handlePictureCardPreview}
              beforeRemove={this.handleRemove}
              fileList={this.fileList.value}
              list-type="picture-card"
              accept="image/png, image/jpeg"
              httpRequest={this.onUpload}
              withCredentials
            >
              <ElIcon>
                <Plus />
              </ElIcon>
            </ElUpload>
          </ElFormItem>
          <ElRow justify="end" align="middle">
            <ElFormItem>
              <ElButton
                type="primary"
                style={{ width: '100px' }}
                onClick={() => this.onSubmitClick(this.formRef)}
              >
                提交
              </ElButton>
            </ElFormItem>
          </ElRow>
          <ElDialog center v-model={this.dialogVisible} width={950}>
            <img
              width={900}
              w-full
              src={this.dialogImageUrl}
              alt="Preview Image"
            />
          </ElDialog>
        </ElForm>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            color: '#fff',
            alignItems: 'center',
          }}
        >
          <span>直接输入Ctrl+V即可上传剪贴板中的图片。</span>
          <ElIcon>
            <Opportunity color="yellow" />
          </ElIcon>
        </div>
      </>
    );
  },
});

export default SubmitProblem;
